import type { TokenStore } from './token-store.js'

/** What `authorizedFetch` needs from the outside world, injected for testability. */
export interface AuthorizedFetchEnv {
  /** The API domain discovery currently trusts (from `discoverApiDomain`). */
  apiDomain: string
  /** The underlying fetch implementation (the global `fetch` in production). */
  fetch: typeof fetch
  /** Where the bearer token is read from. */
  store: TokenStore
}

/**
 * Call the discovered API with the client's bearer token.
 *
 * Two deliberate choices carry the anti-blocking design:
 *  - the token is sent as an explicit `Authorization: Bearer` header, because it must travel to
 *    whatever rotating domain is live now — a cookie set on a previous domain would not; and
 *  - `credentials: 'omit'` guarantees no origin-bound cookie is ever attached, so auth depends
 *    solely on the header we control and nothing leaks across domains.
 *
 * Caller-supplied `init` (method, body, extra headers) is preserved; only the authorization
 * header and credentials mode are imposed.
 */
export const authorizedFetch = async (
  env: AuthorizedFetchEnv,
  path: string,
  init: RequestInit = {},
): Promise<Response> => {
  const token = await env.store.load()

  const headers = new Headers(init.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)

  return env.fetch(`https://${env.apiDomain}${path}`, { ...init, credentials: 'omit', headers })
}
